/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { FaBook, FaTrash, FaFileUpload, FaTimes, FaMoneyBillWave, FaCreditCard, FaCalendarAlt, FaClock, FaLanguage, FaFilter, FaSearch, FaChevronDown, FaCheckCircle, FaSyncAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Toast from '../components/Toast';
import * as authService from '../services/authService'
import * as articleService from '../services/articleService'
import * as masterService from '../services/masterService'
import * as profileService from '../services/profileService'

const SubmitArticleYoungIndia = () => {
  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [runningTitles, setRunningTitles] = useState([]);
  const [filteredTitles, setFilteredTitles] = useState([]);
  const [userSubmissions, setUserSubmissions] = useState([]); // Added to track user's existing submissions
  const [selectedRunningTitle, setSelectedRunningTitle] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [languagesMaster, setLanguagesMaster] = useState([]);
  const [paymentOrderId, setPaymentOrderId] = useState('');
  const [razorpayKey, setRazorpayKey] = useState('');
  const [yiPaymentSettings, setYiPaymentSettings] = useState({ enabled: false, price: 0 });
  const [isUniversityExempt, setIsUniversityExempt] = useState(false);
  const navigate = useNavigate();

  // Load Razorpay Script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // Filter states
  const [filters, setFilters] = useState({
    subject: '',
    subjectId: '',
    stream: '',
    streamId: '',
    specialization: '',
    specializationId: ''
  });

  const [subjects, setSubjects] = useState([]);
  const [streams, setStreams] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [universitiesMaster, setUniversitiesMaster] = useState([]); // Store raw university objects for exemption check
  const [filteredUniversities, setFilteredUniversities] = useState([]);

  // Running title search state
  const [runningTitleSearch, setRunningTitleSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Dropdown visibility states
  const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);
  const [showStreamDropdown, setShowStreamDropdown] = useState(false);
  const [showSpecializationDropdown, setShowSpecializationDropdown] = useState(false);
  const [showRunningTitleDropdown, setShowRunningTitleDropdown] = useState(false);
  const [showUniversityDropdown, setShowUniversityDropdown] = useState(false);
  const [showMentorUniversityDropdown, setShowMentorUniversityDropdown] = useState(false);
  const [universitySearch, setUniversitySearch] = useState('');
  const [mentorUniversitySearch, setMentorUniversitySearch] = useState('');

  // Refs for dropdown containers
  const subjectRef = useRef(null);
  const streamRef = useRef(null);
  const specializationRef = useRef(null);
  const runningTitleRef = useRef(null);
  const universityRef = useRef(null);
  const mentorUniversityRef = useRef(null);
  const lastRunningTitleRequestId = useRef(0);

  // Check if user is logged in
  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser) {
      navigate('/login');
      return;
    }

    loadInitialData();
  }, [navigate]);

  const loadInitialData = async () => {
    try {
      // Wrap profile service call to handle potential 404/errors gracefully
      const safeGetProfile = async () => {
        try {
          return await profileService.getProfile();
        } catch (e) {
          console.error('Profile fetch failed:', e);
          return null;
        }
      };

      const [subjectsData, streamsData, specializationsData, languagesData, universitiesData, submissionsData, profileData] = await Promise.all([
        masterService.getAllSubjects(),
        masterService.getAllStreams(),
        masterService.getAllSpecializations(),
        masterService.getAllLanguages(),
        masterService.getAllUniversities(),
        articleService.getMySubmissions(), // Fetch user's submissions
        safeGetProfile() // Fetch user's profile to auto-fill university
      ]);

      const subjectsArray = Array.isArray(subjectsData) ? subjectsData : (subjectsData?.data || subjectsData?.subjects || []);
      const streamsArray = Array.isArray(streamsData) ? streamsData : (streamsData?.data || streamsData?.streams || []);
      const specializationsArray = Array.isArray(specializationsData) ? specializationsData : (specializationsData?.data || specializationsData?.specializations || []);
      const languagesArray = Array.isArray(languagesData) ? languagesData : (languagesData?.data || languagesData?.languages || []);
      const universitiesRaw = Array.isArray(universitiesData) ? universitiesData : (universitiesData?.data || universitiesData?.universities || []);
      let universitiesArray = [...universitiesRaw];
      const userSubmissionsArray = Array.isArray(submissionsData) ? submissionsData : (submissionsData?.submissions || submissionsData?.articles || submissionsData?.data || []);

      universitiesArray = universitiesArray.map(u => typeof u === 'object' ? u.name : u).filter(Boolean);
      universitiesArray.sort((a, b) => a.localeCompare(b));

      setSubjects(subjectsArray);
      setStreams(streamsArray);
      setSpecializations(specializationsArray);
      setLanguagesMaster(languagesArray);
      setUniversities(universitiesArray);
      setUniversitiesMaster(universitiesRaw);
      setFilteredUniversities(universitiesArray);
      setUserSubmissions(userSubmissionsArray);

      const updateExemptionStatus = (uniName, uniId = null) => {
        if (!uniName && !uniId) {
          setIsUniversityExempt(false);
          return;
        }

        let matchedUni = null;
        if (uniId) {
          // eslint-disable-next-line eqeqeq
          matchedUni = universitiesRaw.find(u => u.id == uniId);
        }

        if (!matchedUni && uniName) {
          matchedUni = universitiesRaw.find(u =>
            u.name && u.name.toLowerCase().trim() === uniName.toLowerCase().trim()
          );
        }

        if (matchedUni) {
          setIsUniversityExempt(!!matchedUni.is_free_yi_article);
        } else {
          setIsUniversityExempt(false);
        }
      };

      // Auto-fill university from profile if available
      if (profileData?.success && profileData?.data?.academic?.university) {
        const userUniversity = profileData.data.academic.university;
        const userUniId = profileData.data.academic.university_id;

        setFormData(prev => ({
          ...prev,
          university: userUniversity
        }));
        setUniversitySearch(userUniversity);

        // Initial check for exemption
        updateExemptionStatus(userUniversity, userUniId);
      }

      // Fetch global YI payment settings
      try {
        const settings = await articleService.getYIArticlePrice();
        if (settings && settings.success) {
          setYiPaymentSettings({
            enabled: settings.enabled,
            price: settings.price
          });
        }
      } catch (err) {
        console.error('Error fetching YI payment settings:', err);
      }
    } catch (error) {
      console.error('Error loading filter data:', error);
      setSubjects([]);
      setStreams([]);
      setSpecializations([]);
      setLanguagesMaster([]);
      setUniversities([]);
      setFilteredUniversities([]);
    }
  };

  const loadRunningTitles = React.useCallback(async (search = '', overrideFilters = null) => {
    const requestId = ++lastRunningTitleRequestId.current;
    try {
      const activeFilters = overrideFilters || filters;
      const params = {
        subject_id: activeFilters.subjectId,
        stream_id: activeFilters.streamId,
        specialization_id: activeFilters.specializationId,
        search: search
      };

      const data = await articleService.getRunningTitles(params);

      if (requestId !== lastRunningTitleRequestId.current) return;

      let titlesArray = Array.isArray(data) ? data : (data?.runningTitles || data?.data || []);

      titlesArray = titlesArray.map(title => {
        let resolvedLanguages = [];

        let rawLanguages = title.languages_allowed || title.language_ids || title.languages || [];

        if (typeof rawLanguages === 'string') {
          try {
            rawLanguages = JSON.parse(rawLanguages);
          } catch (e) {
            console.error('Error parsing languages for title:', title.title, e);
            rawLanguages = [];
          }
        }

        if (Array.isArray(rawLanguages) && rawLanguages.length > 0) {
          resolvedLanguages = rawLanguages.map(langItem => {
            if (typeof langItem === 'object' && langItem.name) {
              return langItem.name;
            }

            const lang = languagesMaster.find(l =>
              (l.id === langItem || l._id === langItem) ||
              (l.code && String(l.code).toLowerCase() === String(langItem).toLowerCase()) ||
              (l.name && String(l.name).toLowerCase() === String(langItem).toLowerCase())
            );

            if (lang) {
              return lang.name;
            } else {
              return langItem;
            }
          }).filter(Boolean);
        }

        return {
          ...title,
          languages: resolvedLanguages
        };
      });

      // Filter out titles the user has already submitted
      const submittedTitleIds = userSubmissions.map(sub => sub.running_title_id || sub.runningTitleId).filter(Boolean);
      const filteredFromSubmissions = titlesArray.filter(title => !submittedTitleIds.includes(title.id));

      setRunningTitles(filteredFromSubmissions);
      setFilteredTitles(filteredFromSubmissions);
    } catch (error) {
      console.error('Error loading running titles:', error);
      showToast('Failed to load running titles', 'error');
      setRunningTitles([]);
      setFilteredTitles([]);
    }
  }, [filters.subjectId, filters.streamId, filters.specializationId, languagesMaster, userSubmissions]);

  useEffect(() => {
    if (languagesMaster.length > 0) {
      loadRunningTitles();
    }
  }, [filters.subjectId, filters.streamId, filters.specializationId, languagesMaster, loadRunningTitles]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (subjectRef.current && !subjectRef.current.contains(event.target)) {
        setShowSubjectDropdown(false);
      }
      if (streamRef.current && !streamRef.current.contains(event.target)) {
        setShowStreamDropdown(false);
      }
      if (specializationRef.current && !specializationRef.current.contains(event.target)) {
        setShowSpecializationDropdown(false);
      }
      if (runningTitleRef.current && !runningTitleRef.current.contains(event.target)) {
        setShowRunningTitleDropdown(false);
      }
      if (universityRef.current && !universityRef.current.contains(event.target)) {
        setShowUniversityDropdown(false);
      }
      if (mentorUniversityRef.current && !mentorUniversityRef.current.contains(event.target)) {
        setShowMentorUniversityDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const renderField = (field) => {
    if (!field) return '';
    if (typeof field === 'object') return field.name || '';
    return field;
  };

  const getLanguageNames = (allowedJson) => {
    try {
      if (!allowedJson) return [];
      const ids = typeof allowedJson === 'string' ? JSON.parse(allowedJson) : allowedJson;
      if (!Array.isArray(ids)) return [];

      return ids.map(id => {
        const lang = languagesMaster.find(l => l.id === id);
        return lang ? lang.name : null;
      }).filter(Boolean);
    } catch (e) {
      console.error('Error parsing languages_allowed:', e);
      return [];
    }
  };

  const getFilteredRunningTitlesForDropdown = () => {
    if (!Array.isArray(filteredTitles)) return [];

    let filtered = [...filteredTitles];

    if (runningTitleSearch) {
      filtered = filtered.filter(title => {
        const titleText = title.title || title.articleTitle || '';
        const subjectText = renderField(title.subject);
        const streamText = renderField(title.stream || title.major);
        const specializationText = renderField(title.specialization);

        return titleText.toLowerCase().includes(runningTitleSearch.toLowerCase()) ||
          (subjectText && subjectText.toLowerCase().includes(runningTitleSearch.toLowerCase())) ||
          (streamText && streamText.toLowerCase().includes(runningTitleSearch.toLowerCase())) ||
          (specializationText && specializationText.toLowerCase().includes(runningTitleSearch.toLowerCase()));
      });
    }

    return filtered;
  };

  const getDaysRemaining = (deadline) => {
    if (!deadline) return null;
    const now = new Date();
    const deadlineDate = new Date(deadline);

    const deadlineStart = new Date(deadlineDate.getFullYear(), deadlineDate.getMonth(), deadlineDate.getDate());
    const nowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const diffTime = deadlineStart - nowStart;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatDeadline = (dateString) => {
    if (!dateString) return 'No deadline set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleFilterChange = (field, value) => {
    if (value && typeof value === 'object') {
      if (field === 'subject') {
        setFilters(prev => ({
          ...prev,
          subject: value.name,
          subjectId: value.id,
          stream: '',
          streamId: '',
          specialization: '',
          specializationId: ''
        }));
      } else if (field === 'stream') {
        setFilters(prev => ({
          ...prev,
          stream: value.name,
          streamId: value.id,
          specialization: '',
          specializationId: ''
        }));
      } else if (field === 'specialization') {
        setFilters(prev => ({
          ...prev,
          specialization: value.name,
          specializationId: value.id
        }));
      }
    } else {
      setFilters(prev => ({
        ...prev,
        [field]: value || '',
        [`${field}Id`]: ''
      }));

      if (field === 'subject' && !value) {
        setFilters(prev => ({
          ...prev,
          subject: '',
          subjectId: '',
          stream: '',
          streamId: '',
          specialization: '',
          specializationId: ''
        }));
      } else if (field === 'stream' && !value) {
        setFilters(prev => ({
          ...prev,
          stream: '',
          streamId: '',
          specialization: '',
          specializationId: ''
        }));
      }
    }

    // Reset running title selection if a filter is changed
    if (selectedRunningTitle) {
      setSelectedRunningTitle(null);
      setRunningTitleSearch('');
      setSelectedLanguage('');
      setFormData(prev => ({
        ...prev,
        paperTitle: '',
        subject: '',
        stream: '',
        specialization: '',
      }));
    }
  };

  const clearFilters = () => {
    setFilters({
      subject: '',
      subjectId: '',
      stream: '',
      streamId: '',
      specialization: '',
      specializationId: ''
    });

    // Reset running title selection when filters are cleared
    if (selectedRunningTitle) {
      setSelectedRunningTitle(null);
      setRunningTitleSearch('');
      setSelectedLanguage('');
      setFormData(prev => ({
        ...prev,
        paperTitle: '',
        subject: '',
        stream: '',
        specialization: '',
      }));
    }

    setShowSubjectDropdown(false);
    setShowStreamDropdown(false);
    setShowSpecializationDropdown(false);
  };

  const handleRunningTitleSelect = (title) => {
    if (title) {
      const displayTitle = title.title || title.articleTitle;
      setSelectedRunningTitle(title);
      setRunningTitleSearch(displayTitle);
      setSelectedLanguage('');

      const subjectName = renderField(title.subject);
      const streamName = renderField(title.stream || title.major);
      const specializationName = renderField(title.specialization);

      setFormData({
        ...formData,
        paperTitle: displayTitle,
        subject: subjectName,
        stream: streamName,
        specialization: specializationName,
      });

      setFilters(prev => ({
        ...prev,
        subject: subjectName,
        subjectId: title.subject_id || title.subject?.id || prev.subjectId,
        stream: streamName,
        streamId: title.stream_id || title.major_id || title.stream?.id || title.major?.id || prev.streamId,
        specialization: specializationName,
        specializationId: title.specialization_id || title.specialization?.id || prev.specializationId
      }));

      setShowRunningTitleDropdown(false);
    }
  };

  const handleRunningTitleSearchChange = (value) => {
    setRunningTitleSearch(value);
    if (!showRunningTitleDropdown) {
      setShowRunningTitleDropdown(true);
    }

    let currentFilters = filters;
    if (selectedRunningTitle) {
      const displayTitle = selectedRunningTitle.title || selectedRunningTitle.articleTitle;
      if (displayTitle && !value.toLowerCase().includes(displayTitle.toLowerCase())) {
        setSelectedRunningTitle(null);
        setSelectedLanguage('');
        setFormData(prev => ({
          ...prev,
          paperTitle: '',
          subject: '',
          stream: '',
          specialization: '',
        }));

        // Immediately clear filters locally to avoid race condition
        currentFilters = {
          subject: '',
          subjectId: '',
          stream: '',
          streamId: '',
          specialization: '',
          specializationId: ''
        };
        setFilters(currentFilters);
      }
    }

    loadRunningTitles(value, currentFilters);
  };

  const handleLanguageSelect = (e) => {
    setSelectedLanguage(e.target.value);
  };

  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    cardHolder: '',
    expiryDate: '',
    cvv: '',
    upiId: '',
  });

  const [paymentMethod, setPaymentMethod] = useState('card');

  const [formData, setFormData] = useState({
    subject: '',
    stream: '',
    specialization: '',
    university: '',
    paperTitle: '',
    mentorName: '',
    mentorDesignation: '',
    mentorUniversity: '',
    mentorPhone: '',
    mentorEmail: '',
    submissionDate: new Date().toISOString().split('T')[0],
    articleText: '',
    keywords: '',
  });

  const currentUser = authService.getCurrentUser();
  const studentName = currentUser ? currentUser.name : '';

  const [uploadedFile, setUploadedFile] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setPaymentData({ ...paymentData, [name]: value });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['.pdf', '.doc', '.docx'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

      if (validTypes.includes(fileExtension)) {
        setUploadedFile(file);
      } else {
        showToast('Please upload only PDF or Word documents', 'error');
        e.target.value = '';
      }
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
  };

  const validateForm = () => {
    if (!selectedRunningTitle) {
      showToast('Please select a running title', 'error');
      return false;
    }

    if (!selectedLanguage || selectedLanguage === '') {
      showToast('Please select the language for your article', 'error');
      return false;
    }

    if (!uploadedFile) {
      showToast('Please upload your article document (PDF or Word)', 'error');
      return false;
    }

    if (!formData.university) {
      showToast('Please select or enter your university', 'error');
      return false;
    }

    return true;
  };

  const handlePayNow = async () => {
    if (!selectedRunningTitle) {
      showToast('Please select a running title first', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const currentUser = authService.getCurrentUser();
      const res = await loadRazorpayScript();
      if (!res) {
        showToast('Payment gateway failed to load. Are you online?', 'error');
        setIsSubmitting(false);
        return;
      }

      const orderData = await articleService.createPreSubmissionOrder(selectedRunningTitle.id);

      if (orderData.success) {
        if (orderData.auto_paid) {
          showToast(orderData.message || 'No payment required for this submission.', 'success');
          setPaymentCompleted(true);
          setPaymentOrderId(orderData.order_id || 'AUTO_PAID');
          setIsSubmitting(false);
          return;
        }

        setRazorpayKey(orderData.key_id);
        // Store for recovery immediately
        localStorage.setItem('last_razorpay_order_id_yi', orderData.order_id);
        const options = {
          key: orderData.key_id,
          amount: orderData.amount * 100,
          currency: orderData.currency,
          name: "Paper Portal",
          description: `Submission fee for ${selectedRunningTitle.title || selectedRunningTitle.articleTitle}`,
          order_id: orderData.order_id,
          modal: {
            ondismiss: function () {
              setIsSubmitting(false);
            }
          },
          handler: async (response) => {
            // Store for recovery
            localStorage.setItem('last_razorpay_order_id_yi', response.razorpay_order_id);
            try {
              const verifyRes = await articleService.verifyPayment({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              });

              if (verifyRes.success) {
                setPaymentOrderId(response.razorpay_order_id);
                setPaymentCompleted(true);
                showToast('Payment successful! You can now submit your article.', 'success');
              }
            } catch (err) {
              console.error('Verification failed', err);
              showToast('Payment verification failed. Please contact support.', 'error');
            }
          },
          prefill: {
            name: currentUser?.full_name || '',
            email: currentUser?.email || '',
            contact: currentUser?.phone_number || ''
          },
          theme: {
            color: "#4F46E5"
          }
        };

        const paymentObject = new window.Razorpay(options);
        paymentObject.open();
      }
    } catch (error) {
      console.error('Payment initialization error:', error);
      showToast(error.message || 'Error initializing payment', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSyncStatus = async () => {
    const lastOrderId = localStorage.getItem('last_razorpay_order_id_yi');
    if (!lastOrderId) {
      showToast('No recent order found to sync. Please pay first.', 'info');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await articleService.syncPaymentStatus(lastOrderId);
      if (res.success) {
        showToast('Payment verified successfully!', 'success');
        setPaymentOrderId(lastOrderId);
        setPaymentCompleted(true);
      } else {
        showToast(res.message || 'Payment not yet confirmed. Please try again.', 'warning');
      }
    } catch (err) {
      showToast(err.message || 'Error syncing payment status', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProceedToPayment = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const isPaymentRequired = yiPaymentSettings.enabled && !isUniversityExempt && yiPaymentSettings.price > 0;
      if (isPaymentRequired && !paymentCompleted) {
        showToast('Please pay the fee before submitting', 'warning');
        return;
      }
      submitArticle();
    }
  };

  const handlePaymentSubmit = (e) => {
    e.preventDefault();

    if (paymentMethod === 'card') {
      if (!paymentData.cardNumber || !paymentData.cardHolder || !paymentData.expiryDate || !paymentData.cvv) {
        showToast('Please fill in all card details', 'error');
        return;
      }
      if (paymentData.cardNumber.replace(/\s/g, '').length !== 16) {
        showToast('Please enter a valid 16-digit card number', 'error');
        return;
      }
      if (paymentData.cvv.length !== 3) {
        showToast('Please enter a valid 3-digit CVV', 'error');
        return;
      }
    } else if (paymentMethod === 'upi') {
      if (!paymentData.upiId) {
        showToast('Please enter your UPI ID', 'error');
        return;
      }
    }

    setIsSubmitting(true);
    setTimeout(() => {
      setPaymentCompleted(true);
      showToast('Payment successful! ₹500 paid', 'success');
      setShowPaymentModal(false);
      setIsSubmitting(false);

      setTimeout(() => {
        submitArticle();
      }, 1000);
    }, 2000);
  };

  const submitArticle = async () => {
    setIsSubmitting(true);

    try {
      const currentUser = authService.getCurrentUser();

      if (!uploadedFile) {
        showToast('Please upload an article document.', 'error');
        setIsSubmitting(false);
        return;
      }

      const formDataPayload = new FormData();

      formDataPayload.append('file', uploadedFile);
      formDataPayload.append('running_title_id', selectedRunningTitle?.id || '');
      formDataPayload.append('title', formData.paperTitle || '');
      formDataPayload.append('language', selectedLanguage || '');

      if (paymentOrderId) {
        formDataPayload.append('razorpay_order_id', paymentOrderId);
      }

      if (formData.university) {
        formDataPayload.append('university', formData.university);
      }
      if (filters.subjectId) {
        formDataPayload.append('subject_id', filters.subjectId);
      }
      if (filters.streamId) {
        formDataPayload.append('stream_id', filters.streamId);
      }
      if (filters.specializationId) {
        formDataPayload.append('specialization_id', filters.specializationId);
      }

      console.log('Submitting article with file:', uploadedFile.name);
      console.log('Running title ID:', selectedRunningTitle?.id);
      console.log('Language:', selectedLanguage);
      console.log('University:', formData.university);

      const response = await articleService.submitArticle(formDataPayload);

      console.log('Article submission response:', response);

      showToast(`Article submitted successfully in ${selectedLanguage}!`, 'success');

      setFormData({
        subject: '',
        stream: '',
        specialization: '',
        university: '',
        paperTitle: '',
        mentorName: '',
        mentorDesignation: '',
        mentorUniversity: '',
        mentorPhone: '',
        mentorEmail: '',
        submissionDate: new Date().toISOString().split('T')[0],
        articleText: '',
        keywords: '',
      });
      setUploadedFile(null);
      setPaymentCompleted(false);
      setPaymentOrderId('');
      setSelectedRunningTitle(null);
      setSelectedLanguage('');
      setRunningTitleSearch('');
      setRunningTitleSearch('');
      clearFilters();
      setShowFilters(false);
      setPaymentData({
        cardNumber: '',
        cardHolder: '',
        expiryDate: '',
        cvv: '',
        upiId: '',
      });
      setIsSubmitting(false);

      loadRunningTitles();

      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (error) {
      console.error('Error submitting article:', error);
      showToast(error.message || 'Error submitting article. Please try again.', 'error');
      setIsSubmitting(false);
    }
  };

  const inputClass = "w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all hover:border-gray-300 text-sm";
  const labelClass = "block text-sm font-semibold text-gray-700 mb-2";
  const sectionBadgeClass = "w-10 h-10 rounded-xl flex items-center justify-center text-lg font-bold shadow-lg text-white";

  // Dark gradient — the new color
  const darkGradient = {
    background: 'linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(49, 46, 129) 50%, rgb(15, 23, 42) 100%)'
  };

  return (
    <>
      {toast && (
        <div className="fixed top-4 right-4 z-[9999]">
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-50 py-6">
        <div className="max-w mx-auto px-4">

          {/* Header — was from-primary-blue to-secondary-blue, now dark gradient */}
          <div className="rounded-2xl shadow-lg p-6 mb-6 relative overflow-hidden" style={darkGradient}>
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20"></div>
            <div className="relative z-10 flex items-center justify-between">
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">Contest Article - Young India</h1>
                <p className="text-indigo-200 text-sm">Submit articles for pre-defined running titles</p>
              </div>
              <div className="hidden md:block">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                  <FaBook className="text-3xl text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Filters Section Removed - Moved inside Select Running Title */}

          {/* Form */}
          <div className="bg-white rounded-2xl shadow-lg">
            <form onSubmit={handleProceedToPayment}>

              {/* Running Title Selection */}
              <div className={`border-b-2 border-gray-100 relative rounded-t-2xl ${!selectedRunningTitle ? 'rounded-b-2xl' : ''}`}>
                {/* Header Band */}
                <div className="p-4 rounded-t-2xl flex flex-col md:flex-row md:items-center justify-between gap-4" style={darkGradient}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg font-bold shadow-lg bg-white text-indigo-900">1</div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Select Running Title</h2>
                      <p className="text-indigo-200 text-sm">Choose from pre-created titles by admin</p>
                    </div>
                  </div>

                  {/* Filter Toggle Button */}
                  <button
                    type="button"
                    onClick={() => !paymentCompleted && setShowFilters(!showFilters)}
                    disabled={paymentCompleted}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${showFilters
                      ? 'bg-white text-indigo-600 shadow-md'
                      : 'bg-white/10 text-white border border-white/20 hover:bg-white/20'
                      } ${paymentCompleted ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <FaFilter className={showFilters ? 'text-indigo-600' : 'text-white'} />
                    {showFilters ? 'Hide Filters' : 'Filter Titles'}
                    {(filters.subject || filters.stream || filters.specialization) && (
                      <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${showFilters ? 'bg-indigo-100 text-indigo-700' : 'bg-white text-indigo-600'
                        }`}>
                        Active
                      </span>
                    )}
                  </button>
                </div>

                {/* Body Content */}
                <div className="p-6 bg-gradient-to-br from-purple-50/30 to-pink-50/30">

                  {/* Collapsible Filters Area */}
                  {showFilters && (
                    <div className="mb-6 p-4 bg-white/80 rounded-xl border border-indigo-100 backdrop-blur-sm shadow-sm transition-all duration-300 relative z-30">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                          <FaFilter className="text-indigo-500" />
                          Refine by Category
                        </h3>
                        {/* Clear Filters Button inside the panel */}
                        {(filters.subject || filters.stream || filters.specialization) && (
                          <button
                            type="button"
                            onClick={() => {
                              clearFilters();
                              // Optional: Keep filters open or close them? User might want to clear and re-select.
                            }}
                            className="text-xs text-red-500 hover:text-red-700 font-medium flex items-center gap-1"
                          >
                            <FaTimes /> Clear All
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Subject Filter */}
                        <div className="relative" ref={subjectRef}>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Department</label>
                          <div className="relative">
                            <input
                              type="text"
                              value={filters.subject}
                              onChange={(e) => {
                                handleFilterChange('subject', e.target.value);
                                setShowSubjectDropdown(true);
                              }}
                              onFocus={() => setShowSubjectDropdown(true)}
                              placeholder="Select Department..."
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-sm"
                            />
                            <FaChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />

                            {/* Subject Dropdown */}
                            {showSubjectDropdown && Array.isArray(subjects) && (
                              <div className="absolute z-[100] mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                                {subjects
                                  .filter(subject =>
                                    subject && subject.name && subject.name.toLowerCase().includes((filters.subject || '').toLowerCase())
                                  )
                                  .map((subject, index) => (
                                    <button
                                      key={index}
                                      type="button"
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleFilterChange('subject', subject);
                                        setShowSubjectDropdown(false);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm hover:bg-indigo-50 hover:text-indigo-700 border-b border-gray-100 last:border-b-0"
                                    >
                                      {subject.name}
                                    </button>
                                  ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Stream Filter */}
                        <div className="relative" ref={streamRef}>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Stream</label>
                          <div className="relative">
                            <input
                              type="text"
                              placeholder={filters.subject ? "Select Stream" : "Select Dept First"}
                              value={filters.stream}
                              onChange={(e) => handleFilterChange('stream', e.target.value)}
                              onFocus={() => {
                                if (filters.subject) setShowStreamDropdown(true);
                                else showToast('Please select a department first', 'info');
                              }}
                              className={`w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-sm ${!filters.subject ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                              disabled={!filters.subject}
                            />
                            <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />

                            {showStreamDropdown && filters.subject && (
                              <div className="absolute z-[100] w-full mt-1 bg-white rounded-xl shadow-xl border border-gray-100 max-h-48 overflow-y-auto">
                                {streams
                                  .filter(s => {
                                    const sSubjectId = s.subject_id || s.subject?.id;
                                    const filterMatch = !filters.subjectId || sSubjectId === filters.subjectId;
                                    const searchMatch = s.name.toLowerCase().includes((filters.stream || '').toLowerCase());
                                    return filterMatch && searchMatch;
                                  })
                                  .map((stream) => (
                                    <button
                                      key={stream.id}
                                      type="button"
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleFilterChange('stream', stream);
                                        setShowStreamDropdown(false);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm hover:bg-indigo-50 transition-colors border-b border-gray-50 last:border-b-0"
                                    >
                                      {stream.name}
                                    </button>
                                  ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Specialization Filter */}
                        <div className="relative" ref={specializationRef}>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Specialization</label>
                          <div className="relative">
                            <input
                              type="text"
                              placeholder={filters.stream ? "Select Specialization" : "Select Stream First"}
                              value={filters.specialization}
                              onChange={(e) => handleFilterChange('specialization', e.target.value)}
                              onFocus={() => {
                                if (filters.stream) setShowSpecializationDropdown(true);
                                else showToast('Please select a stream first', 'info');
                              }}
                              className={`w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-sm ${!filters.stream ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                              disabled={!filters.stream}
                            />
                            <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />

                            {showSpecializationDropdown && filters.stream && (
                              <div className="absolute z-[100] w-full mt-1 bg-white rounded-xl shadow-xl border border-gray-100 max-h-48 overflow-y-auto">
                                {specializations
                                  .filter(s => {
                                    const sStreamId = s.stream_id || s.major_id || s.stream?.id;
                                    const filterMatch = !filters.streamId || sStreamId === filters.streamId;
                                    const searchMatch = s.name.toLowerCase().includes((filters.specialization || '').toLowerCase());
                                    return filterMatch && searchMatch;
                                  }).map((spec, index) => (
                                    <button
                                      key={index}
                                      type="button"
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleFilterChange('specialization', spec);
                                        setShowSpecializationDropdown(false);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm hover:bg-indigo-50 hover:text-indigo-700 border-b border-gray-100 last:border-b-0"
                                    >
                                      {spec.name}
                                    </button>
                                  ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Active Filter Tags (when filters are hidden but active) */}
                  {!showFilters && (filters.subject || filters.stream || filters.specialization) && (
                    <div className="mb-4 flex flex-wrap gap-2 animate-fadeIn">
                      {filters.subject && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 text-slate-700 rounded-md text-xs font-medium border border-slate-200">
                          {filters.subject}
                        </span>
                      )}
                      {filters.stream && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-md text-xs font-medium border border-indigo-100">
                          {filters.stream}
                        </span>
                      )}
                      {filters.specialization && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-50 text-purple-700 rounded-md text-xs font-medium border border-purple-100">
                          {filters.specialization}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="space-y-6 relative z-20">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Running Title Selection Column */}
                      <div className="relative" ref={runningTitleRef}>
                        <label className={labelClass}>Select Running Title <span className="text-red-500">*</span></label>

                        <div className="relative">
                          <input
                            type="text"
                            value={runningTitleSearch}
                            onChange={(e) => !paymentCompleted && handleRunningTitleSearchChange(e.target.value)}
                            onFocus={() => !paymentCompleted && setShowRunningTitleDropdown(true)}
                            placeholder={paymentCompleted ? "Title Locked After Payment" : "Type to search running titles..."}
                            required
                            disabled={paymentCompleted}
                            className={`${inputClass} bg-white pr-10 ${paymentCompleted ? 'bg-gray-50 cursor-not-allowed opacity-70' : ''}`}
                          />
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                            <FaSearch className="text-gray-400" />
                          </div>
                        </div>

                        {/* Running Title Dropdown */}
                        {showRunningTitleDropdown && getFilteredRunningTitlesForDropdown().length > 0 && (
                          <div className="absolute z-[100] mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                            {getFilteredRunningTitlesForDropdown().map((title) => {
                              const daysLeft = getDaysRemaining(title.submissionDeadline);
                              const deadlineText = daysLeft !== null ? ` [${daysLeft} days left]` : '';

                              return (
                                <button
                                  key={title.id}
                                  type="button"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleRunningTitleSelect(title);
                                  }}
                                  className="w-full px-4 py-3 text-left hover:bg-indigo-50 hover:text-indigo-700 border-b border-gray-100 last:border-b-0"
                                >
                                  <div className="font-medium text-gray-900 text-sm">
                                    {title.title || title.articleTitle}
                                    {deadlineText && <span className="text-orange-600 font-normal text-xs ml-2">{deadlineText}</span>}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    {renderField(title.subject)}
                                    {title.stream && ` • ${renderField(title.stream)}`}
                                    {title.specialization && ` • ${renderField(title.specialization)}`}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {/* Selected Running Title Details */}
                        {selectedRunningTitle && (
                          <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                            <p className="text-sm text-gray-700">
                              <strong>Selected:</strong> {selectedRunningTitle.title || selectedRunningTitle.articleTitle}
                            </p>
                            <p className="text-sm text-gray-700 mt-1">
                              <strong>Department:</strong> {renderField(selectedRunningTitle.subject)}
                            </p>
                            {selectedRunningTitle.stream && (
                              <p className="text-sm text-gray-700 mt-1">
                                <strong>Stream:</strong> {renderField(selectedRunningTitle.stream)}
                              </p>
                            )}
                            {selectedRunningTitle.specialization && (
                              <p className="text-sm text-gray-700 mt-1">
                                <strong>Specialization:</strong> {renderField(selectedRunningTitle.specialization)}
                              </p>
                            )}
                            {selectedRunningTitle.languages && selectedRunningTitle.languages.length > 0 && (
                              <p className="text-sm text-gray-700 mt-1">
                                <strong>Languages Available:</strong> {selectedRunningTitle.languages.join(', ')}
                              </p>
                            )}
                            {(selectedRunningTitle.submission_deadline || selectedRunningTitle.submissionDeadline) && (
                              <p className="text-sm text-gray-700 mt-1">
                                <strong>Deadline:</strong> {formatDeadline(selectedRunningTitle.submission_deadline || selectedRunningTitle.submissionDeadline)}
                              </p>
                            )}
                          </div>
                        )}

                        {/* No running titles message */}
                        {getFilteredRunningTitlesForDropdown().length === 0 ? (
                          <div className="mt-4 p-4 bg-orange-50 border-2 border-orange-200 rounded-xl">
                            <div className="flex items-start gap-3">
                              <FaClock className="text-orange-600 text-xl mt-1" />
                              <div>
                                <p className="font-semibold text-orange-800 mb-1">
                                  {runningTitles.length === 0
                                    ? 'No Running Titles Available'
                                    : runningTitleSearch
                                      ? 'No Titles Match Your Search'
                                      : 'No Titles Match Your Filters'}
                                </p>
                                <p className="text-sm text-orange-700">
                                  {runningTitles.length === 0
                                    ? 'All running titles have either expired, been submitted by you, or haven\'t been configured yet. Please contact admin for new running titles.'
                                    : runningTitleSearch
                                      ? 'Try a different search term or clear the search field.'
                                      : 'Try adjusting your filters or clear them to see all available titles.'
                                  }
                                </p>
                                {runningTitles.length > 0 && (
                                  <div className="flex gap-2 mt-2">
                                    {runningTitleSearch && (
                                      <button
                                        type="button"
                                        onClick={() => setRunningTitleSearch('')}
                                        className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all font-medium text-sm"
                                      >
                                        Clear Search
                                      </button>
                                    )}
                                    {(filters.subject || filters.stream || filters.specialization) && (
                                      <button
                                        type="button"
                                        onClick={clearFilters}
                                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all font-medium text-sm"
                                      >
                                        Clear Filters
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-3 text-sm text-gray-600">
                            {getFilteredRunningTitlesForDropdown().length} title{getFilteredRunningTitlesForDropdown().length !== 1 ? 's' : ''} available
                            {runningTitleSearch && ' (filtered by search)'}
                          </div>
                        )}
                      </div>

                      {/* Language Selection Column */}
                      <div>
                        <label className={labelClass}>
                          Select Article Language <span className="text-red-500">*</span>
                          <span className="text-xs font-normal text-gray-500 ml-2">(Choose the language for your article)</span>
                        </label>

                        {selectedRunningTitle ? (
                          <>
                            <select
                              value={selectedLanguage}
                              onChange={handleLanguageSelect}
                              required
                              disabled={paymentCompleted}
                              className={`${inputClass} bg-white ${paymentCompleted ? 'bg-gray-50 cursor-not-allowed opacity-70' : ''}`}
                            >
                              <option value="">-- Select Language --</option>
                              {selectedRunningTitle.languages && selectedRunningTitle.languages.length > 0 ? (
                                selectedRunningTitle.languages.map((language, index) => (
                                  <option key={index} value={language}>
                                    {language}
                                  </option>
                                ))
                              ) : (
                                <option disabled>No languages set for this title</option>
                              )}
                            </select>

                            {selectedLanguage && (
                              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                <p className="text-sm text-gray-700">
                                  <strong>Language Selected:</strong> {selectedLanguage}
                                </p>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="p-4 bg-gray-50 border-2 border-gray-200 rounded-xl text-center">
                            <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                              <FaLanguage className="text-gray-500 text-xl" />
                            </div>
                            <p className="text-sm text-gray-600">
                              Please select a running title first to choose language
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Deadline Information */}
                    {selectedRunningTitle && (selectedRunningTitle.submission_deadline || selectedRunningTitle.submissionDeadline) && (
                      <div className={`p-4 rounded-xl border-2 ${getDaysRemaining(selectedRunningTitle.submission_deadline || selectedRunningTitle.submissionDeadline) <= 3
                        ? 'bg-red-50 border-red-300'
                        : getDaysRemaining(selectedRunningTitle.submission_deadline || selectedRunningTitle.submissionDeadline) <= 7
                          ? 'bg-yellow-50 border-yellow-300'
                          : 'bg-blue-50 border-blue-300'
                        }`}>
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${getDaysRemaining(selectedRunningTitle.submission_deadline || selectedRunningTitle.submissionDeadline) <= 3
                            ? 'bg-red-100'
                            : getDaysRemaining(selectedRunningTitle.submission_deadline || selectedRunningTitle.submissionDeadline) <= 7
                              ? 'bg-yellow-100'
                              : 'bg-blue-100'
                            }`}>
                            {getDaysRemaining(selectedRunningTitle.submission_deadline || selectedRunningTitle.submissionDeadline) <= 3 ? (
                              <FaClock className="text-red-600 text-xl" />
                            ) : (
                              <FaCalendarAlt className={
                                getDaysRemaining(selectedRunningTitle.submission_deadline || selectedRunningTitle.submissionDeadline) <= 7
                                  ? 'text-yellow-600 text-xl'
                                  : 'text-blue-600 text-xl'
                              } />
                            )}
                          </div>
                          <div className="flex-1">
                            <h4 className={`font-bold mb-1 ${getDaysRemaining(selectedRunningTitle.submission_deadline || selectedRunningTitle.submissionDeadline) <= 3
                              ? 'text-red-800'
                              : getDaysRemaining(selectedRunningTitle.submission_deadline || selectedRunningTitle.submissionDeadline) <= 7
                                ? 'text-yellow-800'
                                : 'text-blue-800'
                              }`}>
                              {getDaysRemaining(selectedRunningTitle.submission_deadline || selectedRunningTitle.submissionDeadline) <= 3
                                ? '⚠️ Urgent: Submission Deadline Approaching!'
                                : getDaysRemaining(selectedRunningTitle.submission_deadline || selectedRunningTitle.submissionDeadline) <= 7
                                  ? 'Submission Deadline Coming Soon'
                                  : 'Submission Deadline'}
                            </h4>
                            <p className="text-sm text-gray-700">
                              <strong>Deadline:</strong> {formatDeadline(selectedRunningTitle.submission_deadline || selectedRunningTitle.submissionDeadline)}
                            </p>
                            <p className="text-sm text-gray-700 mt-1">
                              <strong>Time Remaining:</strong>{' '}
                              <span className={`font-bold ${getDaysRemaining(selectedRunningTitle.submission_deadline || selectedRunningTitle.submissionDeadline) <= 3
                                ? 'text-red-700'
                                : getDaysRemaining(selectedRunningTitle.submission_deadline || selectedRunningTitle.submissionDeadline) <= 7
                                  ? 'text-yellow-700'
                                  : 'text-blue-700'
                                }`}>
                                {getDaysRemaining(selectedRunningTitle.submission_deadline || selectedRunningTitle.submissionDeadline)} day
                                {getDaysRemaining(selectedRunningTitle.submission_deadline || selectedRunningTitle.submissionDeadline) !== 1 ? 's' : ''} left
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {selectedRunningTitle && (
                <>
                  {paymentCompleted && (
                    <div className="mx-6 mt-6 p-4 bg-indigo-50 border-2 border-indigo-200 rounded-xl flex items-center gap-3 animate-fadeIn">
                      <FaCheckCircle className="text-indigo-600 text-xl flex-shrink-0" />
                      <p className="text-sm text-indigo-800 font-medium">
                        Payment verified! The form is now locked to prevent changes. Please click <strong>Submit Article</strong> below to complete your submission.
                      </p>
                    </div>
                  )}

                  {/* Article Details */}
                  <div className="p-6 bg-gradient-to-br from-green-50/30 to-emerald-50/30 border-b-2 border-gray-100">
                    <div className="flex items-center gap-3 mb-6">
                      <div className={`${sectionBadgeClass}`} style={darkGradient}>2</div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Article Details</h2>
                        <p className="text-gray-500 text-sm">Review the article title auto-filled from your selection</p>
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className={labelClass}>Article Title</label>
                      <input
                        type="text"
                        value={formData.paperTitle}
                        disabled
                        className={`${inputClass} bg-gray-100 cursor-not-allowed`}
                        placeholder="Auto-filled from running title"
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Submission Date</label>
                      <input
                        type="date"
                        name="submissionDate"
                        value={formData.submissionDate}
                        onChange={handleChange}
                        disabled={paymentCompleted}
                        className={`${inputClass} ${paymentCompleted ? 'bg-gray-50 cursor-not-allowed opacity-70' : ''}`}
                      />
                    </div>
                  </div>

                  {/* Academic Information */}
                  <div className="p-6 border-b-2 border-gray-100">
                    <div className="flex items-center gap-3 mb-6">
                      <div className={`${sectionBadgeClass}`} style={darkGradient}>3</div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Academic Information</h2>
                        <p className="text-gray-500 text-sm">Academic credentials auto-filled from research title</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <label className={labelClass}>Department</label>
                        <input
                          type="text"
                          value={formData.subject}
                          disabled
                          className={`${inputClass} bg-gray-100 cursor-not-allowed`}
                          placeholder="Auto-filled from running title"
                        />
                      </div>

                      <div>
                        <label className={labelClass}>Stream {selectedRunningTitle ? '(Auto-filled)' : '<span className="text-gray-500 text-xs">(Optional)</span>'}</label>
                        <input
                          type="text"
                          name="stream"
                          value={formData.stream}
                          onChange={handleChange}
                          disabled={!!selectedRunningTitle}
                          className={`${inputClass} ${selectedRunningTitle ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
                          placeholder={selectedRunningTitle ? "Auto-filled from title" : "Your stream (optional)"}
                        />
                      </div>

                      <div>
                        <label className={labelClass}>Specialization {selectedRunningTitle ? '(Auto-filled)' : '<span className="text-gray-500 text-xs">(Optional)</span>'}</label>
                        <input
                          type="text"
                          name="specialization"
                          value={formData.specialization}
                          onChange={handleChange}
                          disabled={!!selectedRunningTitle}
                          className={`${inputClass} ${selectedRunningTitle ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
                          placeholder={selectedRunningTitle ? "Auto-filled from title" : "Your specialization (optional)"}
                        />
                      </div>

                      <div>
                        <label className={labelClass}>University <span className="text-red-500">*</span></label>
                        <div className="relative" ref={universityRef}>
                          <input
                            type="text"
                            name="university"
                            value={formData.university}
                            onChange={(e) => {
                              if (!paymentCompleted) {
                                handleChange(e);
                                setUniversitySearch(e.target.value);
                                setShowUniversityDropdown(true);

                                // Optional: Proactively check exemption if the typed name matches exactly
                                const typedName = e.target.value;
                                const matchedUni = universitiesMaster.find(u =>
                                  u.name && u.name.toLowerCase().trim() === typedName.toLowerCase().trim()
                                );
                                setIsUniversityExempt(!!(matchedUni && matchedUni.is_free_yi_article));
                              }
                            }}
                            onFocus={() => {
                              if (!paymentCompleted) {
                                setUniversitySearch('');
                                setShowUniversityDropdown(true);
                              }
                            }}
                            className={`${inputClass} bg-white ${paymentCompleted ? 'bg-gray-50 cursor-not-allowed opacity-70' : ''}`}
                            placeholder={paymentCompleted ? "University Locked" : "Search or Select University *"}
                            required
                            disabled={paymentCompleted}
                            autoComplete="off"
                          />
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-400">
                            <FaChevronDown />
                          </div>

                          {showUniversityDropdown && (
                            <div className="absolute z-[100] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                              {universities.filter(u => u.toLowerCase().includes((universitySearch || formData.university || '').toLowerCase())).length > 0 ? (
                                universities
                                  .filter(u => u.toLowerCase().includes((universitySearch || formData.university || '').toLowerCase()))
                                  .map((uni, index) => (
                                    <div
                                      key={index}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        setFormData({ ...formData, university: uni });
                                        setUniversitySearch(uni);
                                        setShowUniversityDropdown(false);

                                        // Update exemption status when university is selected from dropdown
                                        const matchedUni = universitiesMaster.find(u =>
                                          u.name && u.name.toLowerCase().trim() === uni.toLowerCase().trim()
                                        );
                                        setIsUniversityExempt(!!(matchedUni && matchedUni.is_free_yi_article));
                                      }}
                                      className="px-4 py-2 hover:bg-indigo-50 hover:text-indigo-700 cursor-pointer text-sm text-gray-700"
                                    >
                                      {uni}
                                    </div>
                                  ))
                              ) : (
                                <div className="px-4 py-2 text-sm text-gray-500">No universities found</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Mentor Information */}
                  <div className="p-6 bg-gradient-to-br from-cyan-50/30 to-blue-50/30 border-b-2 border-gray-100">
                    <div className="flex items-center gap-3 mb-6">
                      <div className={`${sectionBadgeClass}`} style={darkGradient}>4</div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Mentor Information <span className="text-gray-500 text-sm font-normal">(Optional)</span></h2>
                        <p className="text-gray-500 text-sm">Research mentor details (all optional)</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                      <div>
                        <label className={labelClass}>Mentor Name <span className="text-gray-500 text-xs">(Optional)</span></label>
                        <input
                          type="text"
                          name="mentorName"
                          value={formData.mentorName}
                          onChange={handleChange}
                          disabled={paymentCompleted}
                          className={`${inputClass} ${paymentCompleted ? 'bg-gray-50 cursor-not-allowed opacity-70' : ''}`}
                          placeholder="Mentor's full name"
                        />
                      </div>

                      <div>
                        <label className={labelClass}>Designation <span className="text-gray-500 text-xs">(Optional)</span></label>
                        <input
                          type="text"
                          name="mentorDesignation"
                          value={formData.mentorDesignation}
                          onChange={handleChange}
                          disabled={paymentCompleted}
                          className={`${inputClass} ${paymentCompleted ? 'bg-gray-50 cursor-not-allowed opacity-70' : ''}`}
                          placeholder="e.g., Professor"
                        />
                      </div>

                      <div>
                        <label className={labelClass}>Mentor University <span className="text-gray-500 text-xs">(Optional)</span></label>
                        <div className="relative" ref={mentorUniversityRef}>
                          <input
                            type="text"
                            name="mentorUniversity"
                            value={formData.mentorUniversity}
                            onChange={(e) => {
                              if (!paymentCompleted) {
                                handleChange(e);
                                setMentorUniversitySearch(e.target.value);
                                setShowMentorUniversityDropdown(true);
                              }
                            }}
                            onFocus={() => {
                              if (!paymentCompleted) {
                                setMentorUniversitySearch('');
                                setShowMentorUniversityDropdown(true);
                              }
                            }}
                            className={`${inputClass} bg-white ${paymentCompleted ? 'bg-gray-50 cursor-not-allowed opacity-70' : ''}`}
                            placeholder={paymentCompleted ? "University Locked" : "Search or Select University"}
                            disabled={paymentCompleted}
                            autoComplete="off"
                          />
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-400">
                            <FaChevronDown />
                          </div>

                          {showMentorUniversityDropdown && (
                            <div className="absolute z-[100] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                              {universities.filter(u => u.toLowerCase().includes((mentorUniversitySearch || formData.mentorUniversity || '').toLowerCase())).length > 0 ? (
                                universities
                                  .filter(u => u.toLowerCase().includes((mentorUniversitySearch || formData.mentorUniversity || '').toLowerCase()))
                                  .map((uni, index) => (
                                    <div
                                      key={index}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        setFormData({ ...formData, mentorUniversity: uni });
                                        setMentorUniversitySearch(uni);
                                        setShowMentorUniversityDropdown(false);
                                      }}
                                      className="px-4 py-2 hover:bg-indigo-50 hover:text-indigo-700 cursor-pointer text-sm text-gray-700"
                                    >
                                      {uni}
                                    </div>
                                  ))
                              ) : (
                                <div className="px-4 py-2 text-sm text-gray-500">No universities found</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className={labelClass}>Mentor Phone <span className="text-gray-500 text-xs">(Optional)</span></label>
                        <input
                          type="tel"
                          name="mentorPhone"
                          value={formData.mentorPhone}
                          onChange={handleChange}
                          maxLength="10"
                          disabled={paymentCompleted}
                          className={`${inputClass} ${paymentCompleted ? 'bg-gray-50 cursor-not-allowed opacity-70' : ''}`}
                          placeholder="10-digit number"
                        />
                      </div>

                      <div>
                        <label className={labelClass}>Mentor Email <span className="text-gray-500 text-xs">(Optional)</span></label>
                        <input
                          type="email"
                          name="mentorEmail"
                          value={formData.mentorEmail}
                          onChange={handleChange}
                          disabled={paymentCompleted}
                          className={`${inputClass} ${paymentCompleted ? 'bg-gray-50 cursor-not-allowed opacity-70' : ''}`}
                          placeholder="mentor@university.edu"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Article Content */}
                  <div className="p-6 border-b-2 border-gray-100">
                    <div className="flex items-center gap-3 mb-6">
                      <div className={`${sectionBadgeClass}`} style={darkGradient}>5</div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Article Content <span className="text-red-500 text-sm font-normal">(Required)</span></h2>
                        <p className="text-gray-500 text-sm">Upload your article document (PDF or Word)</p>
                      </div>
                    </div>

                    {!uploadedFile && (
                      <div className="mb-6">
                        <label className={labelClass}>Upload Document (PDF or Word) <span className="text-red-500 text-xs">(Required)</span></label>
                        <div className="relative">
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleFileUpload}
                            required
                            disabled={paymentCompleted}
                            className={`w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 ${paymentCompleted ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-2">Supported formats: PDF, DOC, DOCX</p>
                      </div>
                    )}

                    {uploadedFile && (
                      <div className="mb-6 p-4 bg-green-50 rounded-lg border-2 border-green-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={darkGradient}>
                              <FaFileUpload className="text-white text-xl" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{uploadedFile.name}</p>
                              <p className="text-sm text-gray-600">
                                {(uploadedFile.size / 1024).toFixed(2)} KB
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => !paymentCompleted && removeFile()}
                            disabled={paymentCompleted}
                            className={`p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all ${paymentCompleted ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title={paymentCompleted ? "Locked after payment" : "Remove file"}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className={labelClass}>Keywords <span className="text-gray-500 text-xs">(Optional)</span></label>
                      <input
                        type="text"
                        name="keywords"
                        value={formData.keywords}
                        onChange={handleChange}
                        disabled={paymentCompleted}
                        className={`${inputClass} ${paymentCompleted ? 'bg-gray-50 cursor-not-allowed opacity-70' : ''}`}
                        placeholder="e.g., machine learning, artificial intelligence, data science"
                      />
                      <p className="text-xs text-gray-500 mt-2">Separate keywords with commas</p>
                    </div>
                  </div>

                  {/* Payment Section */}
                  {(yiPaymentSettings.enabled || isUniversityExempt) && (
                    <div className="p-6 border-b-2 border-gray-100 bg-amber-50/30">
                      <div className="flex items-center gap-3 mb-6">
                        <div className={`${sectionBadgeClass}`} style={darkGradient}>6</div>
                        <div>
                          <h2 className="text-xl font-bold text-gray-900">Payment Details</h2>
                          <p className="text-gray-500 text-sm">Submission fee for this article</p>
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row items-center justify-between p-6 bg-white rounded-xl border-2 border-indigo-100 shadow-sm gap-4">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 bg-indigo-100 rounded-full flex-shrink-0 flex items-center justify-center text-indigo-600">
                            <FaMoneyBillWave className="text-2xl" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">{isUniversityExempt ? 'University Status' : 'Submission Fee'}</p>
                            <p className="text-2xl font-bold text-gray-900">
                              {isUniversityExempt ? (
                                <span className="text-green-600">FREE (Exempted)</span>
                              ) : (
                                !yiPaymentSettings.enabled ? (
                                  <span className="text-green-600">FREE</span>
                                ) : (
                                  `₹${Number(yiPaymentSettings.price).toFixed(2)}`
                                )
                              )}
                            </p>
                          </div>
                        </div>

                        {!isUniversityExempt && yiPaymentSettings.enabled && yiPaymentSettings.price > 0 && (
                          !paymentCompleted ? (
                            <div className="flex flex-col md:flex-row gap-3">
                              {localStorage.getItem('last_razorpay_order_id_yi') && (
                                <button
                                  type="button"
                                  onClick={handleSyncStatus}
                                  disabled={isSubmitting}
                                  className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-bold hover:bg-gray-200 transition-all border border-gray-200"
                                >
                                  <FaSyncAlt className={isSubmitting ? 'animate-spin' : ''} />
                                  Check Status
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={handlePayNow}
                                disabled={isSubmitting}
                                className="flex items-center gap-2 px-8 py-3 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 transition-all shadow-md disabled:opacity-50"
                              >
                                <FaCreditCard />
                                {localStorage.getItem('last_razorpay_order_id_yi') ? 'Retry Payment' : 'Pay Now'}
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 px-6 py-3 bg-green-100 text-green-700 font-bold rounded-lg border-2 border-green-200">
                              <FaCheckCircle className="text-xl" />
                              Payment Verified
                            </div>
                          )
                        )}

                        {(isUniversityExempt || !yiPaymentSettings.enabled || yiPaymentSettings.price <= 0) && (
                          <div className="flex items-center gap-2 px-6 py-3 bg-green-100 text-green-700 font-bold rounded-lg border-2 border-green-200">
                            <FaCheckCircle className="text-xl" />
                            No Payment Required
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="p-6 flex justify-center rounded-b-2xl" style={darkGradient}>
                    <button
                      type="submit"
                      disabled={isSubmitting || (yiPaymentSettings.enabled && !isUniversityExempt && yiPaymentSettings.price > 0 && !paymentCompleted)}
                      className={`flex items-center gap-3 px-12 py-4 bg-white text-indigo-700 text-lg font-bold rounded-xl hover:bg-gray-50 hover:shadow-xl transform hover:scale-105 transition-all shadow-lg ${(isSubmitting || (yiPaymentSettings.enabled && !isUniversityExempt && yiPaymentSettings.price > 0 && !paymentCompleted)) ? 'opacity-50 cursor-not-allowed transform-none' : ''}`}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-700 inline-block mr-2"></div>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <FaFileUpload />
                          Submit Article
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default SubmitArticleYoungIndia;